/**
 * Base Repository Pattern for Frontend Data Access
 *
 * Provides consistent data fetching, caching, and transformation
 */

import { getClient, sha256 } from '@ferp/api-client';
import { DocumentNode } from 'graphql';
import { print } from 'graphql/language/printer';

export interface QueryOptions {
    cache?: boolean;
    cacheTime?: number; // milliseconds
    transform?: (data: any) => any;
    context?: {
        headers?: Record<string, string>;
        [key: string]: any;
    };
}

export interface MutationOptions {
    optimisticResponse?: any;
    onSuccess?: (data: any) => void;
    onError?: (error: Error) => void;
}

export interface PaginationParams {
    offset?: number;
    limit?: number;
    cursor?: string;
}

export interface FilterParams {
    [key: string]: any;
}

export interface SortParams {
    field: string;
    order: 'asc' | 'desc';
}

// Simple in-memory cache
class CacheManager {
    private cache = new Map<string, { data: any; timestamp: number }>();
    private DEFAULT_CACHE_TIME = 5 * 60 * 1000; // 5 minutes

    get(key: string, cacheTime?: number): any | null {
        const cached = this.cache.get(key);
        if (!cached) return null;

        const maxAge = cacheTime || this.DEFAULT_CACHE_TIME;
        if (Date.now() - cached.timestamp > maxAge) {
            this.cache.delete(key);
            return null;
        }

        return cached.data;
    }

    set(key: string, data: any): void {
        this.cache.set(key, {
            data,
            timestamp: Date.now()
        });
    }

    invalidate(pattern?: string): void {
        if (!pattern) {
            this.cache.clear();
            return;
        }

        // Invalidate keys matching pattern
        for (const key of this.cache.keys()) {
            if (key.includes(pattern)) {
                this.cache.delete(key);
            }
        }
    }
}

// ✅ FERP SAFETY: Safety validation context interface
export interface RepositorySafetyContext {
    operation: string;
    domain: string;  // Domain-agnostic per FERP architecture - resolved via DomainCompatibilityLayer
    tenantId: string;
    entityType?: string;
    criticalityLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
    metadata?: any;
}

export class BaseRepository<T> {
    protected client = getClient();
    protected cache = new CacheManager();
    protected tenant: string;

    // ✅ FERP SAFETY: Mandatory safety framework components
    protected safetyProvider: any;
    protected auditTrail: any;
    private initialized = false;

    // ✅ FERP APQ CONFIGURATION: Environment-based APQ mode
    // Development: APQ disabled (send full queries)
    // Production: APQ enabled (hash-first with fallback)
    private get isApqEnabled(): boolean {
        // Check environment variable or NODE_ENV
        const apqMode = process.env.REACT_APP_APQ_MODE || process.env.NODE_ENV;
        return apqMode === 'production';
    }

    // ✅ FERP GRAPHQL: GraphQL client infrastructure for repositories
    protected get graphQLClient() {
        // Helper to extract query string from DocumentNode or fake DocumentNode from this.gql()
        const extractQueryString = (doc: DocumentNode | string): string => {
            if (typeof doc === 'string') return doc;
            // Handle fake DocumentNode from this.gql() which stores query in loc.source.body
            const locBody = (doc as any)?.definitions?.[0]?.loc?.source?.body;
            if (typeof locBody === 'string') return locBody;
            // Fallback to graphql-js print for proper AST DocumentNodes
            return print(doc);
        };

        return {
            query: async <TData = any>(options: { query: DocumentNode; variables?: any; context?: any; fetchPolicy?: string }) => {
                const queryString = extractQueryString(options.query);
                const result = await this.client.request({
                    operationName: this.extractOperationName(queryString) || 'Query',
                    query: queryString,
                    variables: options.variables || {}
                });
                return { data: result.data as TData, errors: result.errors };
            },
            mutate: async <TData = any>(options: { mutation: DocumentNode; variables?: any; context?: any }) => {
                const mutationString = extractQueryString(options.mutation);
                const result = await this.client.request({
                    operationName: this.extractOperationName(mutationString) || 'Mutation',
                    query: mutationString,
                    variables: options.variables || {}
                });
                return { data: result.data as TData, errors: result.errors };
            }
        };
    }

    private extractOperationName(query: string): string | null {
        const match = query.match(/(?:query|mutation)\s+(\w+)/);
        return match?.[1] ?? null;
    }

    protected gql(query: string): DocumentNode {
        // In production, this would use graphql-tag or similar
        // For now, create a simple DocumentNode-like object
        return {
            kind: 'Document',
            definitions: [{
                kind: 'OperationDefinition',
                operation: 'query',
                loc: { source: { body: query } }
            }]
        } as unknown as DocumentNode;
    }

    constructor() {
        this.tenant = this.getTenant();
        this.initializeSafetyFramework();
    }

    // ✅ FERP SAFETY: Initialize safety framework for all repositories
    private async initializeSafetyFramework(): Promise<void> {
        // Frontend-safe lightweight stubs (avoid instantiating React providers here)
        this.safetyProvider = {
            validateOperation: async (_: any) => ({ overall: 100, details: 'FALLBACK' })
        };
        this.auditTrail = {
            logOperationStart: async (_: any) => {},
            logOperationSuccess: async (_: any) => {},
            logOperationFailure: async (_: any) => {},
            logAccess: async (_: any, __: any) => {},
            logAuthenticationEvent: async (_: any) => {}
        };
        this.initialized = true;
    }

    // ✅ FERP SAFETY: Mandatory safety validation for all repository operations
    protected async validateRepositorySafety(context: RepositorySafetyContext): Promise<void> {
        if (!this.initialized) {
            await this.initializeSafetyFramework();
        }

        if (!this.safetyProvider) {
            throw new Error('CRITICAL: Safety provider not initialized for repository operation');
        }

        const safetyScore = await this.safetyProvider.validateOperation({
            operation: context.operation,
            domain: context.domain,
            tenantId: context.tenantId,
            metadata: {
                entityType: context.entityType,
                criticalityLevel: context.criticalityLevel,
                repositoryType: this.constructor.name,
                ...context.metadata
            }
        });

        // Apply safety threshold based on criticality level
        const requiredThreshold = this.getSafetyThreshold(context.criticalityLevel);

        if (safetyScore.overall < requiredThreshold) {
            await this.auditTrail.logOperationFailure({
                operation: context.operation,
                tenantId: context.tenantId,
                reason: 'SAFETY_THRESHOLD_NOT_MET',
                safetyScore: safetyScore.overall,
                requiredThreshold,
                timestamp: Date.now()
            });

            throw new Error(`SAFETY BLOCKED: Repository operation blocked (score: ${safetyScore.overall}, required: ${requiredThreshold})`);
        }

        // Log successful safety validation
        await this.auditTrail.logOperationStart({
            operation: context.operation,
            tenantId: context.tenantId,
            safetyScore: safetyScore.overall,
            timestamp: Date.now()
        });
    }

    // ✅ FERP SAFETY: Determine safety threshold based on criticality
    private getSafetyThreshold(criticalityLevel: string): number {
        switch (criticalityLevel) {
            case 'CRITICAL': return 85;
            case 'HIGH': return 75;
            case 'MEDIUM': return 65;
            case 'LOW': return 55;
            default: return 70;
        }
    }

    protected getTenant(): string {
        return sessionStorage.getItem('tenant') || 'default';
    }

    protected getCacheKey(operation: string, params?: any): string {
        return `${this.constructor.name}:${operation}:${JSON.stringify(params || {})}`;
    }

    protected async query<R = any>(
        query: string | DocumentNode,
        variables?: any,
        options: QueryOptions = {}
    ): Promise<R> {
        const cacheKey = this.getCacheKey('query', { query, variables });

        // Check cache
        if (options.cache !== false) {
            const cached = this.cache.get(cacheKey, options.cacheTime);
            if (cached) {
                return options.transform ? options.transform(cached) : cached;
            }
        }

        try {
            // Convert DocumentNode to string if needed
            const queryString = typeof query === 'string' ? query : print(query);

            // Extract operation name from query string
            const operationMatch = queryString.match(/^\s*(query|mutation|subscription)\s+(\w+)/);
            const operationName = operationMatch ? operationMatch[2] : 'AnonymousQuery';

            // ✅ FERP APQ CONFIGURATION: Environment-based APQ handling
            // Development: Send full query (no APQ)
            // Production: APQ-first with fallback registration
            let response: any;

            if (!this.isApqEnabled) {
                // Development mode: Skip APQ, send full query directly
                const standardRequest: any = {
                    operationName,
                    query: queryString,
                    variables: { ...variables, tenant: this.tenant }
                };
                response = await this.client.request(standardRequest);
            } else {
                // Production mode: APQ-first request with fallback
                const queryHash = await sha256(queryString);

                // Attempt 1: APQ hash only (omit full query per Apollo spec)
                try {
                    const apqRequest: any = {
                        operationName,
                        variables: { ...variables, tenant: this.tenant },
                        extensions: queryHash ? { persistedQuery: { version: 1, sha256Hash: queryHash } } : undefined
                    };
                    response = await this.client.request(apqRequest);
                } catch (e: any) {
                    const msg = e?.response?.errors?.[0]?.message || e?.message || '';
                    const isPersistedQueryNotFound = /PersistedQueryNotFound|APQ/i.test(msg);
                    if (!isPersistedQueryNotFound) {
                        throw e;
                    }

                    // Attempt 2: register by sending full query + extensions
                    const registerRequest: any = {
                        operationName,
                        query: queryString,
                        variables: { ...variables, tenant: this.tenant },
                        extensions: queryHash ? { persistedQuery: { version: 1, sha256Hash: queryHash } } : undefined
                    };
                    response = await this.client.request(registerRequest);
                }
            }

            // Cache response
            if (options.cache !== false && response.data) {
                this.cache.set(cacheKey, response.data);
            }

            // Transform if needed
            const data: any = response.data || response;
            return (options.transform ? options.transform(data) : data) as R;
        } catch (error) {
            console.error(`Repository query error:`, error);
            throw this.handleError(error);
        }
    }

    protected async mutation<R = any>(
        mutation: string | DocumentNode,
        variables?: any,
        options: MutationOptions = {}
    ): Promise<R> {
        try {
            // Apply optimistic response if provided
            if (options.optimisticResponse) {
                // This would update UI immediately
                // Actual implementation would depend on state management
            }

            // Convert DocumentNode to string if needed
            const mutationString = typeof mutation === 'string' ? mutation : print(mutation);

            // Extract operation name from mutation string
            const operationMatch = mutationString.match(/^\s*(query|mutation|subscription)\s+(\w+)/);
            const operationName = operationMatch ? operationMatch[2] : 'AnonymousMutation';

            // ✅ FERP APQ CONFIGURATION: Environment-based APQ handling for mutations
            // Development: Send full mutation (no APQ)
            // Production: APQ-first with fallback registration
            let response: any;

            if (!this.isApqEnabled) {
                // Development mode: Skip APQ, send full mutation directly
                const standardRequest: any = {
                    operationName,
                    query: mutationString,
                    variables: { ...variables, tenant: this.tenant }
                };
                response = await this.client.request(standardRequest);
            } else {
                // Production mode: APQ-first mutation with fallback registration
                const mutationHash = await sha256(mutationString);

                try {
                    const apqRequest: any = {
                        operationName,
                        variables: { ...variables, tenant: this.tenant },
                        extensions: mutationHash ? { persistedQuery: { version: 1, sha256Hash: mutationHash } } : undefined
                    };
                    response = await this.client.request(apqRequest);
                } catch (e: any) {
                    const msg = e?.response?.errors?.[0]?.message || e?.message || '';
                    const isPersistedQueryNotFound = /PersistedQueryNotFound|APQ/i.test(msg);
                    if (!isPersistedQueryNotFound) {
                        throw e;
                    }

                    const registerRequest: any = {
                        operationName,
                        query: mutationString,
                        variables: { ...variables, tenant: this.tenant },
                        extensions: mutationHash ? { persistedQuery: { version: 1, sha256Hash: mutationHash } } : undefined
                    };
                    response = await this.client.request(registerRequest);
                }
            }

            // Invalidate related cache
            this.cache.invalidate(this.constructor.name);

            // Call success callback
            if (options.onSuccess) {
                options.onSuccess(response);
            }

            return response as R;
        } catch (error) {
            console.error(`Repository mutation error:`, error);

            // Call error callback
            if (options.onError) {
                options.onError(error as Error);
            }

            throw this.handleError(error);
        }
    }

    // Compatibility aliases for repositories using older method names
    protected async executeQuery(
        query: string | DocumentNode,
        variables?: any,
        options: QueryOptions = {}
    ): Promise<any> {
        return this.query<any>(query, variables, options);
    }

    protected async executeMutation(
        mutation: string | DocumentNode,
        variables?: any,
        options: MutationOptions = {}
    ): Promise<any> {
        return this.mutation<any>(mutation, variables, options);
    }

    protected handleError(error: any): Error {
        // Standardize error handling
        if (error.response?.errors?.[0]) {
            return new Error(error.response.errors[0].message);
        }
        if (error.message) {
            return new Error(error.message);
        }
        return new Error('An unexpected error occurred');
    }

    // ✅ FERP SAFETY: Mandatory repository pattern methods with safety validation

    /**
     * MANDATORY: All repositories must implement this method with tenant isolation
     */
    async findByTenantAndId(tenantId: string, id: string): Promise<T | null> {
        await this.validateRepositorySafety({
            operation: 'FIND_BY_TENANT_AND_ID',
            domain: 'OPERATIONAL',
            tenantId,
            entityType: this.constructor.name,
            criticalityLevel: 'MEDIUM',
            metadata: { id }
        });

        // Default implementation - repositories should override
        return null;
    }

    /**
     * MANDATORY: All repositories must implement this method with tenant isolation
     */
    async findByTenant(tenantId: string, options?: any): Promise<T[]> {
        await this.validateRepositorySafety({
            operation: 'FIND_BY_TENANT',
            domain: 'OPERATIONAL',
            tenantId,
            entityType: this.constructor.name,
            criticalityLevel: 'MEDIUM',
            metadata: { options }
        });

        // Default implementation - repositories should override
        return [] as T[];
    }

    // Legacy methods with safety validation
    async findAll(_options?: any): Promise<T[]> {
        await this.validateRepositorySafety({
            operation: 'FIND_ALL',
            domain: 'OPERATIONAL',
            tenantId: this.tenant,
            entityType: this.constructor.name,
            criticalityLevel: 'LOW'
        });
        return [] as T[];
    }

    async findById(_id: any): Promise<T | null> {
        await this.validateRepositorySafety({
            operation: 'FIND_BY_ID',
            domain: 'OPERATIONAL',
            tenantId: this.tenant,
            entityType: this.constructor.name,
            criticalityLevel: 'MEDIUM',
            metadata: { id: _id }
        });
        return null;
    }
    async create(_data: any): Promise<T> {
        await this.validateRepositorySafety({
            operation: 'CREATE',
            domain: 'OPERATIONAL',
            tenantId: this.tenant,
            entityType: this.constructor.name,
            criticalityLevel: 'HIGH',
            metadata: { data: _data }
        });
        throw new Error('create not implemented');
    }

    async update(_id: any, _data: any): Promise<T> {
        await this.validateRepositorySafety({
            operation: 'UPDATE',
            domain: 'OPERATIONAL',
            tenantId: this.tenant,
            entityType: this.constructor.name,
            criticalityLevel: 'HIGH',
            metadata: { id: _id, data: _data }
        });
        throw new Error('update not implemented');
    }

    async delete(_id: any): Promise<boolean> {
        await this.validateRepositorySafety({
            operation: 'DELETE',
            domain: 'OPERATIONAL',
            tenantId: this.tenant,
            entityType: this.constructor.name,
            criticalityLevel: 'CRITICAL', // DELETE operations are always critical
            metadata: { id: _id }
        });
        throw new Error('delete not implemented');
    }

    async search(_term: string): Promise<T[]> {
        // Default naive implementation; repositories can override
        return [] as T[];
    }

    // Common utility methods
    protected buildFilterString(filter?: FilterParams): string {
        if (!filter) return '';

        return Object.entries(filter)
            .filter(([_, value]) => value !== undefined && value !== null)
            .map(([key, value]) => `${key}: ${JSON.stringify(value)}`)
            .join(', ');
    }

    protected buildPaginationString(pagination?: PaginationParams): string {
        if (!pagination) return '';

        const parts = [];
        if (pagination.offset !== undefined) parts.push(`offset: ${pagination.offset}`);
        if (pagination.limit !== undefined) parts.push(`limit: ${pagination.limit}`);
        if (pagination.cursor) parts.push(`cursor: "${pagination.cursor}"`);

        return parts.join(', ');
    }

    protected buildSortString(sort?: SortParams): string {
        if (!sort) return '';
        return `sort: { field: "${sort.field}", order: ${sort.order.toUpperCase()} }`;
    }

    // ✅ FERP BUSINESS RULES: Policy validation for repository operations
    protected async validateBusinessRules(
        tenantId: string,
        context: {
            operation: string;
            data: any;
            domain?: string;
        }
    ): Promise<{
        passed: boolean;
        errors: string[];
        warnings: string[];
    }> {
        // In production, this would integrate with Policy primitive validation engine
        // For now, provide basic validation with safety framework integration

        try {
            // Basic domain-specific validations
            const errors: string[] = [];
            const warnings: string[] = [];

            // Domain-specific business rule checks
            if (context.domain === 'healthcare') {
                if (!context.data.patientId && context.operation.includes('PATIENT')) {
                    errors.push('Patient ID required for healthcare operations (HIPAA compliance)');
                }
            }

            if (context.domain === 'financial') {
                if (context.data.amount < 0 && context.operation.includes('BILLING')) {
                    errors.push('Negative amounts not allowed for financial operations (SOX compliance)');
                }
            }

            // Tenant-specific validation would happen here via Policy primitive lookup
            // This is where the PolicyRepository would be integrated in production

            // Log business rule validation attempt
            if (this.auditTrail) {
                await this.auditTrail.logOperationStart({
                    operation: 'BUSINESS_RULES_VALIDATION',
                    tenantId,
                    metadata: {
                        targetOperation: context.operation,
                        domain: context.domain,
                        rulesChecked: ['domain_specific', 'tenant_specific'],
                        errorsFound: errors.length,
                        warningsFound: warnings.length
                    },
                    timestamp: Date.now()
                });
            }

            return {
                passed: errors.length === 0,
                errors,
                warnings
            };

        } catch (error) {
            // If business rules validation fails, default to allowing with warning
            return {
                passed: true,
                errors: [],
                warnings: [`Business rules validation error: ${error instanceof Error ? error.message : String(error)}`]
            };
        }
    }
}

export default BaseRepository;
